/**
 * Kitchen → Account.
 *
 * Replaces AccessCodeSettings.js and AccountSettings.js, which were two
 * `position: fixed` pills docked to the bottom-left corner of the viewport — outside
 * the navigation, outside the layout, discoverable only by already knowing they were
 * there. Their own header comments admitted the placement was chosen because it was a
 * two-line change to a 1,700-line file, not because it was right.
 *
 * The logic in both was sound and is carried over as-is. What changed is where it
 * lives and what was missing around it:
 *
 *   - Change password had NO UI anywhere in the product. `staffSetPassword` has
 *     existed in the Lambda the whole time with zero callers, which is most of why
 *     "self password reset doesn't work".
 *   - Sign out did not exist either — there was no way to leave this screen.
 *   - "Sign out every other device" was claimed by the access-code screen but never
 *     actually happened (access codes and paired devices are revoked by two separate
 *     counters). It is a real, separate action here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  staffGetAccount, staffSetAccountEmails, staffSetCode, staffSetPassword,
  staffUnpairAllDevices, clearStaffSession,
} from '../../menuApi';

const SECTIONS = [
  ['profile', '🏠', 'Restaurant'],
  ['security', '🔒', 'Security'],
  ['notifications', '✉️', 'Notifications'],
  ['payouts', '💳', 'Payments'],
  ['preferences', '⚙️', 'Preferences'],
];

export default function AccountTab({
  section, onSection, restaurant, settings, onSettings, onToast, onSignOut, onOpenSettingsSheet,
}) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setAccount(await staffGetAccount());
    } catch (e) {
      setLoadError((e && e.message) || 'Could not load your account');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (restaurant) load(); else setLoading(false); }, [restaurant, load]);

  // A legacy shared-code session has no restaurant attached, so there is nothing here
  // that can safely be read or changed. Say so rather than rendering dead controls.
  if (!restaurant) {
    return (
      <div className="kp-acct-card">
        <div className="kp-acct-card-head">
          <div className="kp-acct-card-title">Not linked to a restaurant</div>
          <div className="kp-acct-card-sub">
            This screen is signed in with a shared access code, which is being retired.
            Ask RedSpeed for your restaurant's own code to manage your account here.
          </div>
        </div>
        <div className="kp-acct-card-body">
          <button className="kp-act-sec danger" onClick={onSignOut}>Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="kp-acct">
      <nav className="kp-acct-nav">
        {SECTIONS.map(([id, icon, label]) => (
          <button key={id} className={'kp-acct-nav-btn' + (section === id ? ' on' : '')} onClick={() => onSection(id)}>
            <span className="kp-acct-nav-icon">{icon}</span>
            {label}
          </button>
        ))}
      </nav>

      <div className="kp-acct-panel">
        {loadError && <div className="kp-form-error">{loadError}</div>}

        {section === 'profile' && (
          <ProfileSection restaurant={restaurant} account={account} loading={loading} onSignOut={onSignOut} />
        )}
        {section === 'security' && (
          <SecuritySection restaurant={restaurant} onToast={onToast} onSignOut={onSignOut} />
        )}
        {section === 'notifications' && (
          <NotificationsSection account={account} loading={loading} onSaved={setAccount} onToast={onToast} />
        )}
        {section === 'payouts' && (
          <PayoutsSection account={account} loading={loading} />
        )}
        {section === 'preferences' && (
          <PreferencesSection settings={settings} onSettings={onSettings} onOpenSettingsSheet={onOpenSettingsSheet} />
        )}
      </div>
    </div>
  );
}

/* ── Restaurant ───────────────────────────────────────────────────────────── */

function ProfileSection({ restaurant, account, loading, onSignOut }) {
  return (
    <>
      <div className="kp-acct-card">
        <div className="kp-acct-card-head">
          <div className="kp-acct-card-title">Restaurant</div>
          <div className="kp-acct-card-sub">What guests see, and which account this screen is signed in to.</div>
        </div>
        <div className="kp-acct-card-body">
          <div className="kp-acct-kv"><span className="kp-acct-kv-k">Name</span><span className="kp-acct-kv-v">{restaurant.name}</span></div>
          <div className="kp-acct-kv"><span className="kp-acct-kv-k">Restaurant ID</span><span className="kp-acct-kv-v">{restaurant.id || restaurant.restaurantId}</span></div>
          <div className="kp-acct-kv">
            <span className="kp-acct-kv-k">Commission</span>
            <span className="kp-acct-kv-v">{loading ? '—' : `${account?.commissionRate ?? '—'}% of each order`}</span>
          </div>
        </div>
      </div>

      <div className="kp-acct-card">
        <div className="kp-acct-card-head">
          <div className="kp-acct-card-title">This screen</div>
          <div className="kp-acct-card-sub">Signing out returns to the sign-in page. Other devices stay signed in.</div>
        </div>
        <div className="kp-acct-card-body">
          <button className="kp-act-sec" onClick={onSignOut}>Sign out of this screen</button>
        </div>
      </div>
    </>
  );
}

/* ── Security ─────────────────────────────────────────────────────────────── */

function SecuritySection({ restaurant, onToast, onSignOut }) {
  return (
    <>
      <ChangeAccessCode restaurant={restaurant} onToast={onToast} />
      <ChangePassword onToast={onToast} />
      <UnpairDevices onToast={onToast} onSignOut={onSignOut} />
    </>
  );
}

function ChangeAccessCode({ restaurant, onToast }) {
  const [currentCode, setCurrentCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!currentCode || !newCode) { setError('Fill in every field'); return; }
    if (newCode.length < 4) { setError('New code must be at least 4 characters'); return; }
    if (newCode !== confirmCode) { setError('The two new codes do not match'); return; }
    setBusy(true);
    try {
      await staffSetCode(currentCode, newCode);
      setCurrentCode(''); setNewCode(''); setConfirmCode('');
      onToast('Access code changed');
    } catch (e) {
      setError((e && e.message) || 'Could not change the code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kp-acct-card">
      <div className="kp-acct-card-head">
        <div className="kp-acct-card-title">Access code</div>
        {/* This used to promise "every other device has been signed out", which was
            not true — paired tablets validate on a different counter and kept working.
            Say what actually happens, and point at the control that does the rest. */}
        <div className="kp-acct-card-sub">
          The code staff type to sign in to {restaurant.name}. Changing it stops anyone
          using the old code. Tablets already paired stay signed in — use “Sign out all
          devices” below for those.
        </div>
      </div>
      <div className="kp-acct-card-body">
        {error && <div className="kp-form-error">{error}</div>}
        <label className="kp-form-field">
          <span>Current code</span>
          <input type="password" autoComplete="off" value={currentCode} onChange={(e) => { setCurrentCode(e.target.value); setError(''); }} />
        </label>
        <label className="kp-form-field">
          <span>New code</span>
          <input type="password" autoComplete="new-password" value={newCode} onChange={(e) => { setNewCode(e.target.value); setError(''); }} />
          <span className="kp-form-hint">At least 4 characters.</span>
        </label>
        <label className="kp-form-field">
          <span>Confirm new code</span>
          <input type="password" autoComplete="new-password" value={confirmCode}
            onChange={(e) => { setConfirmCode(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </label>
        <div>
          <button className="kp-act-primary" style={{ flex: 'none', padding: '10px 22px' }} disabled={busy} onClick={submit}>
            {busy ? 'Changing…' : 'Change access code'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The screen that did not exist.
 *
 * `staffSetPassword` has been in the Lambda with no caller — not even present in the
 * deployed bundle — so a restaurant literally could not change its own password from
 * anywhere in the product.
 */
function ChangePassword({ onToast }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!current || !next) { setError('Fill in every field'); return; }
    if (next.length < 4) { setError('New password must be at least 4 characters'); return; }
    if (next !== confirm) { setError('The two new passwords do not match'); return; }
    setBusy(true);
    try {
      await staffSetPassword(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      onToast('Password changed');
    } catch (e) {
      setError((e && e.message) || 'Could not change the password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kp-acct-card">
      <div className="kp-acct-card-head">
        <div className="kp-acct-card-title">Password</div>
        <div className="kp-acct-card-sub">
          Used to sign in a new device on the sign-in page. Different from the access
          code above. If you have forgotten it, use “Forgotten your password?” on the
          sign-in screen — it emails a code to your billing address.
        </div>
      </div>
      <div className="kp-acct-card-body">
        {error && <div className="kp-form-error">{error}</div>}
        <label className="kp-form-field">
          <span>Current password</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => { setCurrent(e.target.value); setError(''); }} />
        </label>
        <label className="kp-form-field">
          <span>New password</span>
          <input type="password" autoComplete="new-password" value={next} onChange={(e) => { setNext(e.target.value); setError(''); }} />
        </label>
        <label className="kp-form-field">
          <span>Confirm new password</span>
          <input type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </label>
        <div>
          <button className="kp-act-primary" style={{ flex: 'none', padding: '10px 22px' }} disabled={busy} onClick={submit}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnpairDevices({ onToast, onSignOut }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await staffUnpairAllDevices();
      onToast('Every device signed out');
      clearStaffSession();
      onSignOut();
    } catch (e) {
      onToast((e && e.message) || 'Could not sign out the devices');
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="kp-acct-card kp-acct-danger">
      <div className="kp-acct-card-head">
        <div className="kp-acct-card-title">Sign out all devices</div>
        <div className="kp-acct-card-sub">
          For a tablet that has been lost or stolen. Every paired screen is signed out,
          including this one, and each will need pairing again.
        </div>
      </div>
      <div className="kp-acct-card-body">
        {confirming ? (
          <>
            <div className="kp-form-error">
              This signs out every screen immediately, including the one you are using.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="kp-act-sec" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
              <button className="kp-act-sec danger" disabled={busy} onClick={run}>
                {busy ? 'Signing out…' : 'Yes, sign out everything'}
              </button>
            </div>
          </>
        ) : (
          <div>
            <button className="kp-act-sec danger" onClick={() => setConfirming(true)}>Sign out all devices</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Notifications ────────────────────────────────────────────────────────── */

function NotificationsSection({ account, loading, onSaved, onToast }) {
  const [notifyEmail, setNotifyEmail] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!account) return;
    setNotifyEmail(account.notifyEmail || '');
    setOwnerEmail(account.ownerEmail || '');
  }, [account]);

  const dirty = account && (
    notifyEmail.trim() !== (account.notifyEmail || '')
    || ownerEmail.trim() !== (account.ownerEmail || '')
  );

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const updated = await staffSetAccountEmails({
        notifyEmail: notifyEmail.trim(),
        ownerEmail: ownerEmail.trim(),
      });
      onSaved(updated);
      onToast('Email addresses saved');
    } catch (e) {
      setError((e && e.message) || 'Could not save those addresses');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="kp-acct-card"><div className="kp-acct-card-body">Loading…</div></div>;

  return (
    <div className="kp-acct-card">
      <div className="kp-acct-card-head">
        <div className="kp-acct-card-title">Where GapGo emails you</div>
        <div className="kp-acct-card-sub">Two addresses, because orders and money go to different people.</div>
      </div>
      <div className="kp-acct-card-body">
        {error && <div className="kp-form-error">{error}</div>}
        <label className="kp-form-field">
          <span>Orders inbox</span>
          <input type="email" inputMode="email" autoComplete="off" placeholder="kitchen@yourrestaurant.com.au"
            value={notifyEmail} onChange={(e) => { setNotifyEmail(e.target.value); setError(''); }} />
          <span className="kp-form-hint">Every new order lands here, the moment a guest pays.</span>
        </label>
        <label className="kp-form-field">
          <span>Billing inbox</span>
          <input type="email" inputMode="email" autoComplete="off" placeholder="accounts@yourrestaurant.com.au"
            value={ownerEmail} onChange={(e) => { setOwnerEmail(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && dirty && submit()} />
          <span className="kp-form-hint">
            Payment statements, account notices, and password reset codes. Leave blank to
            use your orders inbox for these too.
          </span>
        </label>
        <div>
          <button className="kp-act-primary" style={{ flex: 'none', padding: '10px 22px' }} disabled={busy || !dirty} onClick={submit}>
            {busy ? 'Saving…' : 'Save addresses'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Payments ─────────────────────────────────────────────────────────────── */

function PayoutsSection({ account, loading }) {
  if (loading) return <div className="kp-acct-card"><div className="kp-acct-card-body">Loading…</div></div>;
  const payouts = account && account.payouts;
  if (!payouts) return <div className="kp-acct-card"><div className="kp-acct-card-body">No payment details yet.</div></div>;

  const state = payouts.payoutsEnabled
    ? { tone: 'ok', label: 'Active', body: 'Your payments are set up. GapGo sends your earnings to your bank account after each settlement.' }
    : payouts.detailsSubmitted
      ? { tone: 'warn', label: 'Under review', body: 'Stripe is reviewing your details. Nothing more is needed from you right now.' }
      : payouts.connected
        ? { tone: 'warn', label: 'Not finished', body: 'Your Stripe setup was started but not completed. RedSpeed can resend your setup link.' }
        : { tone: 'off', label: 'Not set up', body: 'Payments to your bank are not set up yet. RedSpeed will send you a Stripe setup link.' };

  return (
    <div className="kp-acct-card">
      <div className="kp-acct-card-head">
        <div className="kp-acct-card-title">Payments</div>
        <div className="kp-acct-card-sub">{state.body}</div>
      </div>
      <div className="kp-acct-card-body">
        <div className="kp-acct-kv">
          <span className="kp-acct-kv-k">Status</span>
          <span className={'kp-acct-pill ' + state.tone}>{state.label}</span>
        </div>
        <div className="kp-acct-kv">
          <span className="kp-acct-kv-k">GapGo commission</span>
          <span className="kp-acct-kv-v">{account.commissionRate}% of each order</span>
        </div>
      </div>
    </div>
  );
}

/* ── Preferences ──────────────────────────────────────────────────────────── */

function PreferencesSection({ settings, onSettings, onOpenSettingsSheet }) {
  const set = (patch) => onSettings((s) => ({ ...s, ...patch }));

  return (
    <>
      <div className="kp-acct-card">
        <div className="kp-acct-card-head">
          <div className="kp-acct-card-title">Display</div>
          <div className="kp-acct-card-sub">Applies to this screen only, not your other devices.</div>
        </div>
        <div className="kp-acct-card-body">
          <label className="kp-form-field">
            <span>Order layout</span>
            <div className="kp-seg">
              {[['focus', 'Focus'], ['columns', 'Columns']].map(([v, l]) => (
                <button key={v} type="button" className={'kp-seg-opt' + (settings.kitchenView === v ? ' on' : '')}
                  onClick={() => set({ kitchenView: v })}>{l}</button>
              ))}
            </div>
            <span className="kp-form-hint">Focus shows one stage at a time. Columns shows New, Preparing and Ready side by side.</span>
          </label>
          <label className="kp-form-field">
            <span>Text size</span>
            <div className="kp-seg">
              {[['compact', 'Compact'], ['normal', 'Normal'], ['large', 'Large']].map(([v, l]) => (
                <button key={v} type="button" className={'kp-seg-opt' + (settings.textSize === v ? ' on' : '')}
                  onClick={() => set({ textSize: v })}>{l}</button>
              ))}
            </div>
          </label>
        </div>
      </div>

      <div className="kp-acct-card">
        <div className="kp-acct-card-head">
          <div className="kp-acct-card-title">Sound</div>
          <div className="kp-acct-card-sub">The new-order alarm. It repeats until every new order is accepted.</div>
        </div>
        <div className="kp-acct-card-body">
          <div className="kp-acct-kv">
            <span className="kp-acct-kv-k">Alarm</span>
            <button type="button" className={'kp-switch' + (settings.sound ? ' on' : '')}
              onClick={() => set({ sound: !settings.sound })} aria-label="Toggle sound">
              <span className="kp-switch-knob" />
            </button>
          </div>
        </div>
      </div>

      {/* Opening hours, pause rules, timezone and refresh interval still live in the
          settings sheet. Kept there rather than duplicated — the rule editor is a
          substantial surface of its own. */}
      <div className="kp-acct-card">
        <div className="kp-acct-card-head">
          <div className="kp-acct-card-title">Opening hours &amp; more</div>
          <div className="kp-acct-card-sub">Pause rules, timezone, refresh interval and the alarm volume.</div>
        </div>
        <div className="kp-acct-card-body">
          <div>
            <button className="kp-act-sec" onClick={onOpenSettingsSheet}>Open service settings</button>
          </div>
        </div>
      </div>
    </>
  );
}
